import React, { useRef, useState } from 'react';
import { View, ScrollView, TouchableOpacity } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import { isFunction as _isFunction } from 'lodash';
import PropTypes from 'prop-types';

import {
  VFButton,
  VFText,
  Toggle,
  VFImageHeader,
  VFShowMoreText,
  Icon,
} from '@vfgroup-oneplatform/foundation/Components';
import { LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';

import { ConfirmationModalJourney } from '../../../../Common/UI';
import { getThemeImages } from '../../../../Themes';
import SohoFrameworkTestKeys from '../../../../Utils/SohoFrameworkTestKeys';

import {
  ADD_ON_DETAILS_MODES,
  ADD_ON_DETAILS_QUICK_ACTION_MENU_CONFIG,
  ADD_ON_DETAILS_QUICK_ACTION_MENU_MODES,
  ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP,
} from './AddOnDetails.Enum';
import styles from './AddOnDetails.Styles';
import { getDateLabel, getDate, getTotalCost, onShowMoreToggle } from './AddOnDetails.Utils';

function AddOnDetails({
  name,
  title,
  description,
  image,
  price,
  status,
  users,
  validFor,
  duration,
  theme,
  images,
  mode,
  onBack,
  showBack,
  descriptionNumberOfLines,
  withTray,
  onToggleAutoRenew,
  isAutoRenewable,
  isPrimaryButtonDisabled,
  onPrimaryButtonPress,
  onSecondaryButtonPress,
  isCurrencyFirst,
  onUsersPress,
  addUserIcon,
  leftIcon,
  titleStyle,
  withPriceVat,
  onQAConfirmPress,
  onPrimaryModalSuccessPress,
  onSecondaryModalFailurePress,
  vfImageHeaderProps,
  withUsers,
  getPrimaryButtonTitle,
  getSecondaryButtonTitle,
  QAConfig,
  withButtons,
  withStartDate,
  withPrice,
  withAutoRenew,
  ...props
}) {
  const CMJRef = useRef(undefined);
  const SohoImages = getThemeImages(theme.name);

  const [autoRenew, setAutoRenew] = useState(isAutoRenewable);

  const shouldShowDateSection =
    (!!validFor && mode === ADD_ON_DETAILS_MODES.REMOVE) ||
    (withStartDate && mode === ADD_ON_DETAILS_MODES.BUY) ||
    [ADD_ON_DETAILS_MODES.REQUEST, ADD_ON_DETAILS_MODES.CANCEL_REQUEST].includes(mode);

  const shouldShowPriceInfo =
    withPrice &&
    withUsers &&
    (mode === ADD_ON_DETAILS_MODES.REMOVE ||
      (mode === ADD_ON_DETAILS_MODES.BUY && users.length > 0));

  const shouldShowAutoRenewToggle =
    withAutoRenew &&
    [ADD_ON_DETAILS_MODES.BUY, ADD_ON_DETAILS_MODES.REQUEST, ADD_ON_DETAILS_MODES.REMOVE].includes(
      mode,
    );

  const withExtraDetails =
    shouldShowDateSection || shouldShowPriceInfo || shouldShowAutoRenewToggle;

  const priceAndCurrency = isCurrencyFirst
    ? `${price?.currency}${price?.value}`
    : `${price?.value}${price?.currency}`;

  const _onSecondaryButtonPress = () => {
    onSecondaryButtonPress(mode, onActionPress);
  };
  const _onPrimaryButtonPress = () => {
    onPrimaryButtonPress(mode, onActionPress);
  };

  const renderPriceInfo = () => {
    if (shouldShowPriceInfo) {
      return (
        <View style={styles.priceInfoContainerStyle(theme)}>
          <View>
            <VFText
              type="primary"
              i18nKey="soho_addons_remove_addon_total_cost_label"
              style={styles.priceInfoTitleTextStyle}
            />
            <VFText
              type="primary"
              i18nKey="soho_addons_remove_addon_based_on_label"
              placeHolders={[`${users.length}`]}
              style={styles.priceInfoSubtitleTextStyle}
            />
          </View>

          <View>
            <VFText
              type="primary"
              i18nKey={getTotalCost(users.length, price, isCurrencyFirst)}
              style={styles.priceTextStyle}
            />
            {withPriceVat ? (
              <VFText
                type="primary"
                i18nKey="soho_addons_buy_addons_inc_vat_label"
                style={styles.vatTextStyle}
              />
            ) : null}
          </View>
        </View>
      );
    }
  };

  const _onToggleAutoRenew = value => {
    setAutoRenew(value);
    _isFunction(onToggleAutoRenew) && onToggleAutoRenew(value, isAutoRenewable);
  };

  const renderAutoRenewToggle = () => {
    if (shouldShowAutoRenewToggle) {
      return (
        <View style={styles.rowStyle(theme)}>
          <VFText
            type="primary"
            i18nKey="soho_addons_buy_addon_auto_renew_label"
            style={styles.rowText}
          />
          <Toggle
            onChange={_onToggleAutoRenew}
            initialValue={isAutoRenewable}
            inactiveContainerStyle={styles.toggleContainerStyle}
          />
        </View>
      );
    }
  };

  const renderDateSection = () => {
    if (shouldShowDateSection) {
      return (
        <View style={styles.rowStyle(theme)}>
          <VFText type="primary" i18nKey={getDateLabel(mode, autoRenew)} style={styles.rowText} />
          <VFText
            type="primary"
            i18nKey={getDate(mode, validFor, duration)}
            style={styles.rowText}
          />
        </View>
      );
    }
  };

  const renderSecondaryButton = modeType => {
    if ([ADD_ON_DETAILS_MODES.REMOVE, ADD_ON_DETAILS_MODES.CANCEL_REQUEST].includes(modeType)) {
      return (
        <VFButton
          type="secondary"
          title={getSecondaryButtonTitle(modeType)}
          style={styles.secondaryButton}
          onPress={_onSecondaryButtonPress}
          testKey={SohoFrameworkTestKeys.AddOnDetailsSecondaryBtn}
        />
      );
    }
  };

  const renderContent = () => {
    return (
      <>
        <VFImageHeader
          image={image}
          title={name}
          showBack={showBack}
          onBack={onBack}
          titleStyle={titleStyle}
          showClose={false}
          withOverlay={false}
          {...vfImageHeaderProps}
        />
        <ScrollView contentContainerStyle={styles.scrollContainer(withTray)}>
          <VFText type="primary" style={styles.headerText} i18nKey={title} />

          <View style={styles.priceContainer}>
            <VFText type="primary" i18nKey={priceAndCurrency} style={styles.priceValue} />
            <VFText
              type="primary"
              i18nKey="soho_addons_cost_per_month"
              style={styles.durationText}
            />
          </View>
          <View style={styles.description(theme)}>
            <VFShowMoreText
              text={description}
              style={styles.descriptionText}
              showLessText="soho_addons_addon_description_show_less"
              showMoreText="soho_addons_addon_description_show_more"
              numberOfLines={descriptionNumberOfLines}
              onShowMoreToggle={onShowMoreToggle}
            />
          </View>
          {withUsers && (
            <View style={styles.usersSectionStyle(theme)}>
              <View style={styles.usersContainerStyle}>
                <VFText
                  type="primary"
                  i18nKey={
                    users.length === 1
                      ? 'soho_addons_user_count_format'
                      : 'soho_addons_users_count_format'
                  }
                  placeHolders={[`${users.length}`]}
                  style={styles.usersText}
                />
                <TouchableOpacity style={styles.addUserContainerStyle} onPress={onUsersPress}>
                  <VFText
                    i18nKey={'soho_addons_add_users_button'}
                    style={styles.addUserTextStyle}
                    type="textWithIcon"
                  />
                  <Icon
                    size={26}
                    name={addUserIcon ? images?.[addUserIcon] : SohoImages.ic_users_new}
                    style={styles.addUserIconStyle}
                  />
                </TouchableOpacity>
              </View>
              <VFText i18nKey="soho_addons_users_hint" style={styles.usersHintText} />
            </View>
          )}

          {withExtraDetails && (
            <>
              {renderPriceInfo()}
              {renderDateSection()}
              {renderAutoRenewToggle()}
            </>
          )}

          {withButtons && (
            <View style={styles.buttonsContainerStyle}>
              <VFButton
                type="primary"
                title={getPrimaryButtonTitle(mode)}
                disabled={isPrimaryButtonDisabled}
                onPress={_onPrimaryButtonPress}
                testKey={SohoFrameworkTestKeys.AddOnDetailsPrimaryBtn}
              />
              {renderSecondaryButton(mode)}
            </View>
          )}
        </ScrollView>
      </>
    );
  };

  const onActionPress = QAmode => {
    const shouldShowQAMHint =
      (!!validFor && QAmode === ADD_ON_DETAILS_QUICK_ACTION_MENU_MODES.REMOVE) ||
      (withStartDate && QAmode === ADD_ON_DETAILS_QUICK_ACTION_MENU_MODES.BUY) ||
      [
        ADD_ON_DETAILS_QUICK_ACTION_MENU_MODES.REQUEST,
        ADD_ON_DETAILS_QUICK_ACTION_MENU_MODES.EDIT,
      ].includes(QAmode);

    CMJRef.current.setConfigs({
      confirmationQuickActionConfig: {
        ...QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.CONFIRM],
        descriptionPlaceHolders: [name],
        hint:
          shouldShowQAMHint &&
          QAConfig[QAmode]?.[ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.CONFIRM].hint,
        ...(QAmode === ADD_ON_DETAILS_QUICK_ACTION_MENU_MODES.REMOVE && {
          hintPlaceHolders: [validFor?.endDate],
        }),
      },
      loadingScreenConfig: QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.LOADING],
      successConfigScreen: {
        ...QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.SUCCESS],
        descriptionPlaceHolders: [name],
        primaryButtonProps: {
          ...QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.SUCCESS].primaryButtonProps,
          onPress: onPrimaryModalSuccessPress,
        },
      },
      failureConfigScreen: {
        ...QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.FAILURE],
        primaryButtonProps:
          QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.FAILURE].primaryButtonProps,
        secondaryButtonProps: {
          ...QAConfig[QAmode][ADD_ON_DETAILS_QUICK_ACTION_MENU_STEP.FAILURE].secondaryButtonProps,
          onPress: onSecondaryModalFailurePress,
        },
      },
      dataToBeSent: {
        QAmode,
      },
    });
    CMJRef.current.openQAM();
  };

  return (
    <>
      {renderContent()}
      <ConfirmationModalJourney ref={CMJRef} functionToBeExecuted={onQAConfirmPress} />
    </>
  );
}

AddOnDetails.defaultProps = {
  descriptionNumberOfLines: 3,
  isCurrencyFirst: false,
  withPriceVat: false,
  isAutoRenewable: true,
  showBack: true,
  users: [],
  theme: LightThemeColors,
  withUsers: true,
  QAConfig: ADD_ON_DETAILS_QUICK_ACTION_MENU_CONFIG,
  withStartDate: true,
  withPrice: true,
  withAutoRenew: true,
  withButtons: true,
};

AddOnDetails.propTypes = {
  onQAConfirmPress: PropTypes.func,
  onPrimaryModalSuccessPress: PropTypes.func,
  onSecondaryModalFailurePress: PropTypes.func,
  onBack: PropTypes.func,
  showBack: PropTypes.bool,
  withTray: PropTypes.bool,
  images: PropTypes.object,
  mode: PropTypes.string,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      role: PropTypes.string,
      '@type': PropTypes.string,
    }),
  ),
  id: PropTypes.string,
  status: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  duration: PropTypes.shape({
    amount: PropTypes.string,
    unit: PropTypes.string,
    units: PropTypes.string,
  }),
  name: PropTypes.string,
  validFor: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string,
  }),
  price: PropTypes.shape({
    type: PropTypes.string,
    recurringPeriod: PropTypes.string,
    unitOfMeasure: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
  }),
  icon: PropTypes.any,
  image: PropTypes.any,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  descriptionNumberOfLines: PropTypes.number,
  onToggleAutoRenew: PropTypes.func,
  isAutoRenewable: PropTypes.bool,
  isPrimaryButtonDisabled: PropTypes.bool,
  onPrimaryButtonPress: PropTypes.func,
  onSecondaryButtonPress: PropTypes.func,
  isCurrencyFirst: PropTypes.bool,
  onUsersPress: PropTypes.func,
  addUserIcon: PropTypes.string,
  withPriceVat: PropTypes.bool,
  leftIcon: PropTypes.string,
  titleStyle: ViewPropTypes.style,
  vfImageHeaderProps: PropTypes.object,
  withUsers: PropTypes.bool,
  withButtons: PropTypes.bool,
  QAConfig: PropTypes.object,
  getPrimaryButtonTitle: PropTypes.func,
  getSecondaryButtonTitle: PropTypes.func,
  withStartDate: PropTypes.bool,
  withPrice: PropTypes.bool,
  withAutoRenew: PropTypes.bool,
};
export default AddOnDetails;
